/**
 * Counselling checklist schemas.
 *
 * v1 (legacy): the 13-item Kroll checklist — the shape persisted on counseling
 * records created before the OCP round-2 feedback. Kept for rendering old
 * records and for the backend entity parity.
 *
 * v2 (current): per-ISEC-question points keyed by the stable question ids in
 * constants/isecQuestions.ts (John Park's set, July 3 UAT feedback: checkbox +
 * free-form note beside each point, identical in CV and the counseling queue).
 * New records persist the v2 shape; `toIsecPoints()` converts either shape for
 * editing.
 *
 * Pure data module (no React/MUI imports) so specs and other consumers can use
 * it without pulling in the CounselingDialog component graph.
 */
import type { IsecPoints } from '../../constants/isecQuestions';
import { EMPTY_ISEC_POINTS, ISEC_POINT_IDS } from '../../constants/isecQuestions';

export interface CounselingChecklistItem {
  checked: boolean;
  notes: string;
}

/** v1 legacy shape — matches the 13 Kroll counseling checklist items. */
export interface CounselingChecklist {
  takenBefore: CounselingChecklistItem;
  whatMedicationFor: CounselingChecklistItem;
  howToTake: CounselingChecklistItem;
  whenAndHowLong: CounselingChecklistItem;
  specialAdminInstructions: CounselingChecklistItem;
  potentialSideEffects: CounselingChecklistItem;
  interactionsWithOtherMeds: CounselingChecklistItem;
  missedDose: CounselingChecklistItem;
  howToKnowWorking: CounselingChecklistItem;
  properStorage: CounselingChecklistItem;
  refillsHowToRequest: CounselingChecklistItem;
  effectOnOtherConditions: CounselingChecklistItem;
  selfMonitoringTechniques: CounselingChecklistItem;
}

export const CHECKLIST_LABELS: Record<keyof CounselingChecklist, string> = {
  takenBefore: 'Have you taken this medication before',
  whatMedicationFor: 'What the medication is for',
  howToTake: 'How to take the medication',
  whenAndHowLong: 'When to take the medication and for how long',
  specialAdminInstructions: 'Special administration instructions',
  potentialSideEffects: 'Potential side effects',
  interactionsWithOtherMeds: 'Interactions with other meds (Rx & OTC), food and alcohol',
  missedDose: 'What to do if dose is missed',
  howToKnowWorking: 'How to know that medication is working',
  properStorage: 'Proper storage',
  refillsHowToRequest: 'Refills - when and how to request',
  effectOnOtherConditions: 'Effect on other conditions (if applicable)',
  selfMonitoringTechniques: 'Self-monitoring techniques',
};

export const CHECKLIST_KEYS = Object.keys(CHECKLIST_LABELS) as (keyof CounselingChecklist)[];

export const EMPTY_CHECKLIST: CounselingChecklist = Object.fromEntries(
  CHECKLIST_KEYS.map((k) => [k, { checked: false, notes: '' }]),
) as unknown as CounselingChecklist;

/**
 * Where each legacy v1 item lives in the ISEC question set. Every v1 key MUST
 * map to a valid question id (enforced by isecChecklistGroups.spec.ts) so no
 * documentation is dropped when an old record is opened for editing.
 */
export const LEGACY_CHECKLIST_TO_POINT_ID: Record<keyof CounselingChecklist, string> = {
  takenBefore: 'ind-taken-before',
  whatMedicationFor: 'ind-purpose',
  effectOnOtherConditions: 'ind-other-conditions',
  howToKnowWorking: 'eff-signs-working',
  selfMonitoringTechniques: 'eff-self-monitoring',
  howToTake: 'eff-how-to-take',
  whenAndHowLong: 'eff-schedule-duration',
  specialAdminInstructions: 'saf-admin-instructions',
  potentialSideEffects: 'saf-side-effects',
  interactionsWithOtherMeds: 'saf-interactions',
  missedDose: 'saf-missed-dose',
  properStorage: 'saf-storage',
  refillsHowToRequest: 'com-refill-process',
};

const POINT_ID_SET = new Set(ISEC_POINT_IDS);

/**
 * Normalize a stored checklist (v1 legacy keys, v2 point ids, or a mix) into
 * the full v2 per-point shape for editing. Unknown keys are dropped; when a
 * legacy key and a point id both target the same point, checked/notes are
 * merged (checked wins, notes concatenated).
 */
export function toIsecPoints(
  stored: Record<string, Partial<CounselingChecklistItem> | null | undefined> | null | undefined,
): IsecPoints {
  const points: IsecPoints = Object.fromEntries(
    ISEC_POINT_IDS.map((id) => [id, { checked: false, notes: '' }]),
  );
  if (!stored) return points;
  for (const [key, value] of Object.entries(stored)) {
    if (!value || typeof value !== 'object') continue;
    const pointId = POINT_ID_SET.has(key)
      ? key
      : LEGACY_CHECKLIST_TO_POINT_ID[key as keyof CounselingChecklist];
    if (!pointId) continue;
    const target = points[pointId];
    const notes = typeof value.notes === 'string' ? value.notes : '';
    points[pointId] = {
      checked: target.checked || !!value.checked,
      notes:
        target.notes && notes && target.notes !== notes
          ? `${target.notes} / ${notes}`
          : notes || target.notes,
    };
  }
  return points;
}

/** A point counts as documented once it is checked OR carries a note. */
export function isPointDocumented(
  item: Partial<CounselingChecklistItem> | null | undefined,
): boolean {
  return !!item && (!!item.checked || (item.notes || '').trim().length > 0);
}

/**
 * Fill `current` from `incoming` — ADDITIVELY. A point is taken from `incoming`
 * only while the corresponding point in `current` is still undocumented, so the
 * person typing right now always wins and nothing is ever cleared.
 *
 * GP-1488: the same rule governs both directions of "document once" — reading a
 * finalized Task Queue counselling session back into the Clinical Verification
 * ISEC card, and seeding the counselling sheet from ISEC documented at CV — so
 * it lives here once instead of being re-implemented on each surface (see
 * CLAUDE.md: one source of truth for shared data).
 *
 * Returns `current` unchanged (same reference) when there is nothing to fill,
 * which makes it safe to call from a React effect without looping.
 */
export function mergeIsecPoints(
  current: IsecPoints | null | undefined,
  incoming: IsecPoints | null | undefined,
): IsecPoints {
  const base: IsecPoints = { ...EMPTY_ISEC_POINTS, ...(current || {}) };
  if (!incoming) return (current as IsecPoints) ?? base;
  let changed = false;
  const next: IsecPoints = { ...base };
  for (const id of ISEC_POINT_IDS) {
    const donor = incoming[id];
    if (!isPointDocumented(donor)) continue;
    if (isPointDocumented(base[id])) continue;
    next[id] = { checked: !!donor.checked, notes: donor.notes || '' };
    changed = true;
  }
  return changed ? next : ((current as IsecPoints) ?? base);
}

export { EMPTY_ISEC_POINTS };
export type { IsecPoints };
